import { useMemo, useState } from 'react';

import {
  ArrowDownUp,
  Building2,
  CalendarDays,
  CheckCircle2,
  Eye,
  Scale,
  Send,
  XCircle,
} from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { formatarData } from '@/nucleo/formato/datas';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { STATUS_TONE, type StatusTone } from '@/nucleo/statusBadgeTokens';
import { cn } from '@/nucleo/utilitarios';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FieldTooltip } from '@/ui/FieldTooltip';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/base/dialog';
import { Label } from '@/ui/base/label';
import { MultiSelectCombobox } from '@/ui/base/multi-select-combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';

import { useDres } from '../hooks/useDres';
import { useRevisoesSme } from './dados';
import type { DecisaoRevisao, OrdenacaoRevisao, RevisaoMedicaoSme, StatusRevisao } from './tipos';

/**
 * Pedidos de revisão (SME) — `/sme/revisoes`.
 *
 * A fila que a SME analisa quando o credenciado discorda do valor apurado numa
 * competência: ela lê o motivo, emite parecer e decide entre aceitar, rejeitar
 * ou encaminhar ao DTP para recálculo.
 *
 * A LEITURA É MOCKADA E A DECISÃO NÃO GRAVA. Não há endpoint de revisão de
 * medição em `apps/api/src/modulos/` nem tabela no banco — o dado de leitura
 * vem de `dados.ts` sob a exceção registrada em `CLAUDE.md` para esta tela, e
 * "Registrar decisão" mostra o aviso honesto de indisponibilidade em vez de
 * fingir que gravou (mesmo padrão de `admin/parametros/slaAprovacaoSme`).
 * Além do aviso passageiro, o diálogo traz um aviso fixo: aviso que some
 * deixa quem audita uma captura de tela concluir que a decisão foi registrada.
 *
 * DUAS DIVERGÊNCIAS CONSCIENTES EM RELAÇÃO AO PROTÓTIPO:
 *
 * 1. **O cartão inteiro é o alvo do clique, e não um botão "Ver" ao lado.**
 *    CLAUDE.md, regras de interface, item 9. O rótulo do protótipo
 *    ("Analisar"/"Ver") continua visível, dentro do próprio alvo.
 * 2. **O filtro de DREs fica sempre visível.** O protótipo alterna entre o
 *    filtro e um selo de contexto conforme o escopo de DRE do usuário
 *    (`useDresEscopo`/`DreContextChip`), e este sistema não tem conceito de
 *    DRE no `auth` — inventar o escopo aqui seria criar infraestrutura de
 *    papel sem decisão registrada. Decisão do dono em 2026-09-22.
 */

const AJUDA_VALOR_CALCULADO =
  'Valor apurado pelo motor (quantidades × tarifa). Base do espelho — ainda pode receber ajustes antes da aprovação final.';

const APRESENTACAO_DO_STATUS: Record<StatusRevisao, { rotulo: string; tom: StatusTone }> = {
  PENDENTE: { rotulo: 'Pendente', tom: 'warning' },
  EM_ANALISE: { rotulo: 'Em análise', tom: 'info' },
  ACEITA: { rotulo: 'Aceita', tom: 'success' },
  REJEITADA: { rotulo: 'Rejeitada', tom: 'destructive' },
  ENCAMINHADA_DTP: { rotulo: 'Encaminhada ao DTP', tom: 'neutral' },
};

const OPCOES_DE_STATUS = [
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'EM_ANALISE', label: 'Em análise' },
  { value: 'ACEITA', label: 'Aceita' },
  { value: 'REJEITADA', label: 'Rejeitada' },
  { value: 'ENCAMINHADA_DTP', label: 'Encaminhada DTP' },
];

const FILTROS_PADRAO = {
  rv_busca: '',
  rv_mes: '',
  rv_dre: '',
  rv_status: 'PENDENTE',
};

const CAMPOS_DE_FILTRO: FilterFieldConfig[] = [
  {
    key: 'rv_busca',
    label: 'Buscar',
    type: 'search',
    placeholder: 'Credenciado, CPF/CNPJ...',
    flex: true,
  },
  {
    key: 'rv_status',
    label: 'Status',
    type: 'select',
    options: OPCOES_DE_STATUS,
    allOptionLabel: 'Todos',
    width: 'w-full sm:w-[200px]',
  },
];

/** `2026-08` vira `08/2026` — como o protótipo escreve a competência na lista. */
function competenciaLegivel(competencia: string): string {
  const [ano, mes] = competencia.split('-');
  if (!ano || !mes) return '—';
  return `${mes}/${ano}`;
}

/** O status do pedido é enum cru; o selo mostra o rótulo em português. */
function SeloDeStatus({ status }: { status: StatusRevisao }) {
  const { rotulo, tom } = APRESENTACAO_DO_STATUS[status];
  return (
    <Badge variant="outline" className={cn('font-medium', STATUS_TONE[tom])}>
      {rotulo}
    </Badge>
  );
}

export function RevisoesSmeListPage() {
  const { values, setFilter, clearAll } = useUrlFilters(FILTROS_PADRAO);
  const { data: dres = [] } = useDres();

  const [ordenacao, definirOrdenacao] = useState<OrdenacaoRevisao>('DATA_ASC');
  const [emAnalise, definirEmAnalise] = useState<RevisaoMedicaoSme | null>(null);
  const [decisao, definirDecisao] = useState<DecisaoRevisao>('ACEITA');
  const [parecer, definirParecer] = useState('');

  const dreIds = useMemo(
    () => (values.rv_dre ? values.rv_dre.split(',').filter(Boolean) : []),
    [values.rv_dre],
  );

  const consulta = useRevisoesSme({
    status: (values.rv_status || 'TODOS') as StatusRevisao | 'TODOS',
    dreIds,
    busca: values.rv_busca,
  });

  const revisoes = useMemo(() => {
    let base = consulta.data ?? [];
    if (values.rv_mes) {
      base = base.filter((r) => r.medicao.mesReferencia === values.rv_mes);
    }
    return [...base].sort((a, b) => {
      if (ordenacao === 'VALOR_DESC') {
        return Number(b.medicao.valorTotal) - Number(a.medicao.valorTotal);
      }
      const esquerda = new Date(a.criadaEm).getTime();
      const direita = new Date(b.criadaEm).getTime();
      return ordenacao === 'DATA_ASC' ? esquerda - direita : direita - esquerda;
    });
  }, [consulta.data, values.rv_mes, ordenacao]);

  const contagens = useMemo(
    () => ({
      total: revisoes.length,
      pendentes: revisoes.filter((r) => r.status === 'PENDENTE').length,
      encaminhadas: revisoes.filter((r) => r.status === 'ENCAMINHADA_DTP').length,
      respondidas: revisoes.filter((r) => r.status === 'ACEITA' || r.status === 'REJEITADA').length,
    }),
    [revisoes],
  );

  const opcoesDeDre = useMemo(
    () =>
      dres.map((d) => ({
        value: d.id,
        label: d.nome ?? d.codigo ?? d.id,
        code: d.codigo ?? undefined,
      })),
    [dres],
  );

  function abrirAnalise(revisao: RevisaoMedicaoSme) {
    definirEmAnalise(revisao);
    definirDecisao('ACEITA');
    definirParecer('');
  }

  function registrarDecisao() {
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pedidos de revisão"
        description="Pedidos de revisão de medição enviados pelos credenciados. Analise, emita parecer e encaminhe ao DTP quando necessário."
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <CartaoDeContagem rotulo="Total filtrado" valor={contagens.total} />
        <CartaoDeContagem rotulo="Pendentes" valor={contagens.pendentes} destacado />
        <CartaoDeContagem rotulo="Encaminhadas DTP" valor={contagens.encaminhadas} />
        <CartaoDeContagem rotulo="Já respondidas" valor={contagens.respondidas} />
      </div>

      <FilterBar
        fields={CAMPOS_DE_FILTRO}
        values={values}
        defaultValues={FILTROS_PADRAO}
        onChange={setFilter}
        onClear={clearAll}
      >
        <div className="space-y-1">
          <Label className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground">
            <Building2 className="h-3 w-3" aria-hidden="true" /> DREs
          </Label>
          <MultiSelectCombobox
            options={opcoesDeDre}
            selected={dreIds}
            onChange={(escolhidas) => setFilter('rv_dre', escolhidas.join(','))}
            placeholder="Todas as DREs"
            searchPlaceholder="Buscar DRE..."
            emptyMessage="Nenhuma DRE encontrada."
            ariaLabel="DREs"
            renderSelectedText={(quantas) =>
              `${quantas} ${quantas === 1 ? 'DRE selecionada' : 'DREs selecionadas'}`
            }
            triggerClassName="w-full sm:w-[240px]"
          />
        </div>

        <div className="space-y-1">
          <Label
            htmlFor="rv-competencia"
            className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"
          >
            <CalendarDays className="h-3 w-3" aria-hidden="true" /> Competência
          </Label>
          <MonthFilter
            id="rv-competencia"
            hideLabel
            value={values.rv_mes}
            onChange={(mes) => setFilter('rv_mes', mes)}
          />
        </div>

        <div className="space-y-1">
          <Label
            htmlFor="rv-ordenacao"
            className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"
          >
            <ArrowDownUp className="h-3 w-3" aria-hidden="true" /> Ordenar por
          </Label>
          <Select
            value={ordenacao}
            onValueChange={(valor) => definirOrdenacao(valor as OrdenacaoRevisao)}
          >
            <SelectTrigger id="rv-ordenacao" className="w-full sm:w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DATA_ASC">Mais antigos primeiro</SelectItem>
              <SelectItem value="DATA_DESC">Mais recentes primeiro</SelectItem>
              <SelectItem value="VALOR_DESC">Maior valor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </FilterBar>

      {consulta.isLoading ? (
        <Spinner rotulo="Carregando pedidos de revisão" />
      ) : consulta.isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void consulta.refetch() }}
        />
      ) : revisoes.length === 0 ? (
        <EmptyState
          icon={Scale}
          title="Nenhuma revisão encontrada"
          description="Não há pedidos de revisão para os filtros selecionados."
        />
      ) : (
        <ul className="grid list-none gap-3 p-0">
          {revisoes.map((revisao) => (
            <li key={revisao.id}>
              <CartaoDeRevisao revisao={revisao} onAbrir={() => abrirAnalise(revisao)} />
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={emAnalise !== null}
        onOpenChange={(aberto) => !aberto && definirEmAnalise(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Análise da revisão</DialogTitle>
            <DialogDescription>
              {emAnalise
                ? `${emAnalise.medicao.credenciado.nome} — Competência ${competenciaLegivel(emAnalise.medicao.mesReferencia)}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          {emAnalise && (
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Motivo informado pelo credenciado</CardTitle>
                </CardHeader>
                <CardContent className="whitespace-pre-wrap text-sm">
                  {emAnalise.motivo}
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="inline-flex items-center text-xs uppercase text-muted-foreground">
                    Valor calculado
                    <FieldTooltip text={AJUDA_VALOR_CALCULADO} />
                  </p>
                  <p className="font-semibold">{formatarDinheiro(emAnalise.medicao.valorTotal)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Status atual</p>
                  <SeloDeStatus status={emAnalise.status} />
                </div>
              </div>

              {emAnalise.parecerSme && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Parecer SME (anterior)</CardTitle>
                  </CardHeader>
                  <CardContent className="whitespace-pre-wrap text-sm">
                    {emAnalise.parecerSme}
                  </CardContent>
                </Card>
              )}

              {emAnalise.status === 'PENDENTE' && (
                <>
                  <Alert>
                    <AlertTitle>Esta decisão ainda não é registrada</AlertTitle>
                    <AlertDescription>
                      A resposta ao credenciado entra em funcionamento junto com o restante do ciclo
                      de medição. Nada do que for preenchido aqui é gravado.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label htmlFor="rv-decisao">Decisão *</Label>
                    <Select
                      value={decisao}
                      onValueChange={(valor) => definirDecisao(valor as DecisaoRevisao)}
                    >
                      <SelectTrigger id="rv-decisao">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACEITA">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
                            Aceitar e devolver ao credenciado
                          </span>
                        </SelectItem>
                        <SelectItem value="REJEITADA">
                          <span className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-destructive" aria-hidden="true" />
                            Rejeitar
                          </span>
                        </SelectItem>
                        <SelectItem value="ENCAMINHADA_DTP">
                          <span className="flex items-center gap-2">
                            <Send className="h-4 w-4" aria-hidden="true" />
                            Encaminhar ao DTP
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rv-parecer">Parecer da SME * (mín. 5 caracteres)</Label>
                    <Textarea
                      id="rv-parecer"
                      rows={4}
                      value={parecer}
                      onChange={(evento) => definirParecer(evento.target.value)}
                      placeholder="Descreva a análise técnica realizada..."
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => definirEmAnalise(null)}>
              Fechar
            </Button>
            {emAnalise?.status === 'PENDENTE' && (
              <Button onClick={registrarDecisao}>Registrar decisão</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CartaoDeContagem({
  rotulo,
  valor,
  destacado = false,
}: {
  rotulo: string;
  valor: number;
  destacado?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase text-muted-foreground">{rotulo}</p>
        <p className={cn('text-2xl font-bold', destacado && 'text-primary')}>{valor}</p>
      </CardContent>
    </Card>
  );
}

/**
 * O cartão inteiro é o alvo do clique — CLAUDE.md, regras de interface, item 9.
 * O rótulo do protótipo ("Analisar" quando pendente, "Ver" depois) fica dentro
 * do alvo, como texto, e não como segundo controle ao lado dele.
 */
function CartaoDeRevisao({
  revisao,
  onAbrir,
}: {
  revisao: RevisaoMedicaoSme;
  onAbrir: () => void;
}) {
  const { medicao } = revisao;
  const { credenciado } = medicao;
  const acao = revisao.status === 'PENDENTE' ? 'Analisar' : 'Ver';

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={onAbrir}
          aria-label={`${acao} o pedido de revisão de ${credenciado.nome}, competência ${competenciaLegivel(medicao.mesReferencia)}`}
          className="flex w-full flex-col gap-3 rounded-lg p-4 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring md:flex-row md:items-center md:gap-4"
        >
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <SeloDeStatus status={revisao.status} />
              <span className="text-sm text-muted-foreground">
                Competência {competenciaLegivel(medicao.mesReferencia)}
              </span>
              <span className="text-xs text-muted-foreground">• DRE {credenciado.dre.codigo}</span>
              <span className="text-xs text-muted-foreground">
                • Solicitado em {formatarData(revisao.criadaEm)}
              </span>
            </div>
            <span className="block truncate font-semibold">{credenciado.nome}</span>
            <p className="text-xs text-muted-foreground">
              {credenciado.cpfCnpj} · Valor calculado:{' '}
              <strong>{formatarDinheiro(medicao.valorTotal)}</strong>
            </p>
            <p className="mt-2 line-clamp-2 text-sm">
              <span className="text-muted-foreground">Motivo do credenciado: </span>
              {revisao.motivo}
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 self-start rounded-md border border-input px-3 py-1.5 text-sm font-medium md:self-center">
            <Eye className="h-4 w-4" aria-hidden="true" />
            {acao}
          </span>
        </button>
      </CardContent>
    </Card>
  );
}
